import DeleteIcon from '@material-ui/icons/Delete';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useHistory, useLocation } from 'react-router-dom';
import { CloudDownload } from '@material-ui/icons';
import GenericList from 'components/List/GenericList';
import { EntityColumnType } from 'components/List/TableCellWithConfigurableStyle';
import { useExpeditionsWithFilter } from 'hooks';
import { Routes } from 'routes';
import { TableFilterContext } from 'tableFilterContext';
import { AppContext } from 'context';
import { useLatestClosureDate } from 'utils/closureHooks';
import EditIcon from 'components/Icons/EditIcon';
import { Expedition, ExpeditionFiltersEncoded, UserRole } from 'types';
import { checkDateUpperTimeInterval, createUTCDateFromDateString } from 'utils/date';
import ActionButton from 'components/Buttons/ActionButton';
import DeleteConfirmationDialog from 'components/Generic/DeleteConfirmationDialog';
import { deleteExpedition, exportConfirmationShippingSlip, exportShippingSlip } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { formatExpeditionQueryAsFilter } from 'utils/filters';
import { generateNotifyFromViolations, isSubmissionErrorType } from 'services/errors';
import { ExpeditionFilterBar } from './ExpeditionFilterBar';
import { colorFilterMiseEnCoherence } from './ExpeditionFilterBar.style';
import {
  FILTER_MISE_COHERENCE_CENTRE_TRI,
  FILTER_MISE_COHERENCE_DIFFERENTE,
  FILTER_MISE_COHERENCE_REPRENEUR,
} from './constants';

const columns = [
  {
    columnPath: '',
    headerTranslationKey: 'expeditions.action',
    type: 'action' as EntityColumnType,
    disableSort: true,
    style: {
      columnWidth: '5%',
    },
  },
  {
    columnPath: 'expeditionDate',
    headerTranslationKey: 'expeditions.expeditionDate',
    type: 'date' as EntityColumnType,
    style: {
      columnWidth: '20%',
      paddingLeft: '3rem',
    },
  },
  {
    columnPath: 'expeditionDate',
    headerTranslationKey: 'expeditions.expeditionTime',
    type: 'hour' as EntityColumnType,
    disableSort: true,
    style: {
      columnWidth: '12%',
    },
  },
  {
    columnPath: 'buyer.name',
    headerTranslationKey: 'expeditions.buyer',
    style: {
      columnWidth: '15%',
    },
  },
  {
    columnPath: 'material.shortName',
    headerTranslationKey: 'expeditions.material',
    style: {
      columnWidth: '15%',
    },
  },
  {
    columnPath: 'conditioningQuantity',
    headerTranslationKey: 'expeditions.conditioningQuantity',
    type: 'number' as EntityColumnType,
    style: {
      columnWidth: '18%',
    },
  },
  {
    columnPath: 'weight',
    headerTranslationKey: 'expeditions.weight',
    type: 'quantity' as EntityColumnType,
    style: {
      columnWidth: '20%',
      paddingRight: '7rem',
    },
  },
  {
    columnPath: 'expeditionBuyerConfirmation.weight',
    headerTranslationKey: 'expeditions.confirmedWeight',
    type: 'quantity' as EntityColumnType,
    style: {
      columnWidth: '10%',
      paddingRight: '7rem',
    },
  },
  {
    columnPath: 'isMiseEnCoherenceAccepted',
    headerTranslationKey: 'expeditions.repartition',
    chipsConfiguration: {
      null: {
        backgroundColor: colorFilterMiseEnCoherence[FILTER_MISE_COHERENCE_CENTRE_TRI],
        translationKeyFromValue: 'expeditions.repartitionLabel.centreTri',
        hideIfPropertiesHaveValues: { expeditionBuyerConfirmation: null },
        tooltipTextTranslationKey: 'expeditions.repartitionTooltip.centreTri',
      },
      false: {
        backgroundColor: colorFilterMiseEnCoherence[FILTER_MISE_COHERENCE_DIFFERENTE],
        translationKeyFromValue: 'expeditions.repartitionLabel.differente',
        tooltipTextTranslationKey: 'expeditions.repartitionTooltip.differente',
      },
      true: {
        backgroundColor: colorFilterMiseEnCoherence[FILTER_MISE_COHERENCE_REPRENEUR],
        translationKeyFromValue: 'expeditions.repartitionLabel.repreneur',
        tooltipTextTranslationKey: 'expeditions.repartitionTooltip.repreneur',
      },
    },
    type: 'chipsConfigured' as EntityColumnType,
    style: {
      columnWidth: '10%',
      paddingRight: '7rem',
    },
  },
];
type ExpeditionListProps = {
  setExportDisabled: (isExportDisabled: boolean) => void;
};
const ExpeditionList: React.FC<ExpeditionListProps> = ({ setExportDisabled }) => {
  const { search, page, itemsPerPage, order, orderBy } = useContext(TableFilterContext);
  const location = useLocation();
  const history = useHistory();

  const query: ExpeditionFiltersEncoded = search ? JSON.parse(atob(search)) : {};
  const filters = formatExpeditionQueryAsFilter(query);

  const [expeditionList, total] = useExpeditionsWithFilter({ page, itemsPerPage }, { order, orderBy }, filters);

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const [closureMonth] = useLatestClosureDate(sortingCenter);

  const minStartDate = sortingCenter?.initializationDate ? new Date(sortingCenter.initializationDate) : new Date();
  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));

  const { t } = useTranslation();
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const shouldEnableExport = Boolean(expeditionList && expeditionList.length > 0);

  useEffect(() => {
    setExportDisabled(!shouldEnableExport);
  }, [shouldEnableExport, setExportDisabled]);

  return (
    <>
      <DeleteConfirmationDialog
        title={t('expeditionCreateForm.deletion.confirm')}
        isOpen={Boolean(confirmDeleteId)}
        setOpen={() => setConfirmDeleteId(null)}
        confirmDeletion={async () => {
          if (!confirmDeleteId) return;
          setConfirmDeleteId(null);
          try {
            await deleteExpedition(confirmDeleteId.toString());
            notify({
              message: t('expeditionCreateForm.deletion.success'),
              severity: 'success',
            });
          } catch (e: any) {
            if (isSubmissionErrorType(e) && e.status === 412 && e.response.body.violations) {
              generateNotifyFromViolations(e.response.body.violations, t);
            } else {
              notify({
                message: t('errorGeneric'),
                severity: 'error',
              });
            }
          }
        }}
      />
      <div style={{ marginTop: 30 }}>
        <ExpeditionFilterBar
          minStartDate={minStartDate}
          maxEndDate={new Date()}
          defaultStartDate={minStartDate}
          defaultEndDate={new Date()}
        />
      </div>
      <GenericList
        entities={expeditionList}
        columns={columns}
        total={total}
        detailsLink={Routes.Expeditions}
        generateAvailableActions={(expedition: Expedition) => {
          const actions = [];

          const linkTo = {
            pathname: generatePath(Routes.ExpeditionEdit, { expeditionId: expedition.id }),
            state: { referer: location.pathname },
          };

          const expeditionDate = createUTCDateFromDateString(expedition.expeditionDate);
          const disabled = readOnly || checkDateUpperTimeInterval(expeditionDate, closureMonth);
          const isEditable = disabled || expedition.isMiseEnCoherenceAccepted;
          const expeditionId = expedition.id.toString();

          actions.push({
            title: t('update'),
            onClick: () => history.push(linkTo.pathname, linkTo.state),
            disabled,
            action: (
              <ActionButton
                variant="outlined"
                icon={EditIcon}
                component={Link}
                data-qa-id={'edit-button'}
                to={linkTo}
                disabled={isEditable}
              />
            ),
          });

          actions.push({
            title: t('expeditions.export.shippingSlip'),
            onClick: () => exportShippingSlip(expeditionId, t),
            disabled: false,
            action: (
              <ActionButton
                variant="outlined"
                icon={CloudDownload}
                data-qa-id={'export-button'}
                onClick={() => exportShippingSlip(expeditionId, t)}
              />
            ),
          });

          if (null !== expedition.expeditionBuyerConfirmation) {
            actions.push({
              title: t('expeditions.export.confirmationShippingSlip'),
              onClick: () => exportConfirmationShippingSlip(expeditionId, t),
              disabled: false,
              action: (
                <ActionButton
                  variant="outlined"
                  icon={CloudDownload}
                  data-qa-id={'export-button'}
                  onClick={() => exportConfirmationShippingSlip(expeditionId, t)}
                />
              ),
            });
          }

          actions.push({
            title: t('delete'),
            onClick: () => setConfirmDeleteId(expedition.id),
            disabled,
            action: (
              <ActionButton
                variant="outlined"
                icon={DeleteIcon}
                data-qa-id={'delete-button'}
                onClick={() => setConfirmDeleteId(expedition.id)}
                disabled={disabled}
              />
            ),
          });

          return actions;
        }}
      />
    </>
  );
};
export default ExpeditionList;
